import { NavLink, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { currentUser, signOut } from '../lib/auth';

const linkBase =
  'px-3 py-1.5 rounded-full transition-colors text-[14px]';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const items: NavItem[] = [
  { to: '/',         label: 'Dashboard', end: true },
  { to: '/courses',  label: 'Courses' },
  { to: '/notes',    label: 'Notes' },
  { to: '/cohort',   label: 'Cohort' },
  { to: '/account',  label: 'Account' },
];

/**
 * Floating pill nav. Same visual language as the marketing site header — a
 * rounded bar that floats over the aurora bg with backdrop-blur. We keep it
 * sticky so the lesson reader's left/right rails stay accessible while
 * scrolling the long center column.
 */
export function NavTopbar() {
  const user = currentUser();
  const navigate = useNavigate();

  function handleSignOut() {
    signOut();
    navigate('/login', { replace: true });
  }

  return (
    <header className="sticky top-3 lg:top-4 z-50 px-3 sm:px-5 mt-3 lg:mt-4">
      <nav className="mx-auto max-w-[1200px] flex items-center justify-between gap-4 pl-3 pr-2 py-2 rounded-full bg-paper/90 backdrop-blur-md border border-rule shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)]">
        <NavLink to="/" className="flex items-center gap-2 pl-1" aria-label="Thren — home">
          <span aria-hidden="true" className="w-7 h-7 rounded-md bg-ink text-paper grid place-items-center font-extrabold text-[14px] leading-none">T</span>
          <span className="font-bold tracking-tight text-[17px] text-ink">Thren</span>
        </NavLink>

        <nav className="hidden md:flex items-center gap-1 text-ink/80">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                clsx(
                  linkBase,
                  isActive
                    ? 'text-ink bg-paper2 font-medium'
                    : 'hover:bg-paper2 hover:text-ink',
                )
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-paper2 border border-rule">
              <span
                className="w-6 h-6 rounded-full bg-gradient-to-br from-accent-500 to-candy-pink grid place-items-center text-[11px] font-bold"
                aria-hidden="true"
              >
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </span>
              <span className="text-[12px] text-ink/80 max-w-[100px] truncate">{user.name || user.email}</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="btn-ghost text-[13px]"
            aria-label="Sign out"
          >
            Sign out
          </button>
        </div>
      </nav>
    </header>
  );
}
